import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { supabase } from '@/src/lib/supabase';
import { LucideIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import { PromptSuggestion } from '@/src/lib/types';
import { timeAwareGreeting, dayOfWeekIn } from '@/src/lib/format';
import { useWeather } from '@/src/hooks/useWeather';
import { useAppColors } from '@/src/lib/theme';
import { useVenueSetupFields } from '@/src/hooks/useVenueSetupFields';

interface WelcomeProps {
  firstName: string;
  venueName: string;
  timezone?: string;
  drawerId: string;
  venueId?: string;
  userId?: string;
  onSelectSuggestion: (prompt: string) => void;
  onOpenPanel?: () => void;
  openPanelLabel?: string;
  userRole?: string;
  /** The message box, shown under the greeting until the chat starts. */
  composer?: React.ReactNode;
}

const SUGGESTIONS_BY_DRAWER: Record<string, PromptSuggestion[]> = {
  general: [
    { title: "What's happening today?", description: 'Forecast, tasks and who’s on', icon: 'Calendar' },
    { title: 'Brief me for tonight', description: 'Service prep summary', icon: 'Zap' },
    { title: 'Anything I should know?', description: 'Alerts and patterns', icon: 'Info' },
  ],
  communications: [
    { title: 'Summarise unread threads', description: 'What needs my attention', icon: 'Inbox' },
    { title: 'Draft a note to the team', description: 'Quick announcement', icon: 'Send' },
    { title: 'Find supplier-line activity', description: 'Recent supplier comms', icon: 'PhoneCall' },
  ],
  roster: [
    { title: "Who's on this week?", description: 'Next 7 days at a glance', icon: 'Calendar' },
    { title: "Anyone running late?", description: 'Track shift starts', icon: 'Clock' },
    { title: 'Find a cover for tomorrow', description: 'Available staff list', icon: 'Users' },
  ],
  stock: [
    { title: "What's running low?", description: 'Items below par level', icon: 'AlertCircle' },
    { title: 'Order from a supplier', description: 'Draft a purchase order', icon: 'ShoppingCart' },
    { title: 'Show suppliers + lead times', description: 'Contacts and turnaround', icon: 'Truck' },
  ],
  reports: [
    { title: 'Brief me on last week', description: 'Revenue, covers, top items', icon: 'BarChart' },
    { title: "Generate today's daily report", description: 'End-of-shift summary', icon: 'FileText' },
    { title: 'Compare the last 4 weeks', description: 'Trend overview', icon: 'TrendingUp' },
  ],
  training: [
    { title: "Who's overdue on training?", description: 'Compliance check', icon: 'AlertTriangle' },
    { title: 'Show RSA certifications', description: 'Cert status by staff', icon: 'Award' },
    { title: 'Add a training module', description: 'Upload SOP or video', icon: 'GraduationCap' },
  ],
  messages: [
    { title: "What's in my inbox?", description: 'Recent threads', icon: 'Inbox' },
    { title: 'Draft a shift handover', description: 'Note to next manager', icon: 'MessageSquare' },
    { title: 'Open task chats', description: 'Active task threads', icon: 'MessageCircle' },
  ],
  foh: [
    { title: "Tonight's opening checklist", description: 'Pre-service prep', icon: 'ClipboardList' },
    { title: 'Add a FOH task', description: 'Assign to floor staff', icon: 'Plus' },
    { title: "Who's on the floor tonight?", description: "Tonight's roster", icon: 'Users' },
  ],
  boh: [
    { title: 'Kitchen prep status', description: "What's done, what's not", icon: 'ChefHat' },
    { title: 'Add a BOH task', description: 'Prep, cleaning, deliveries', icon: 'Plus' },
    { title: '86 a menu item', description: 'Mark sold out', icon: 'Ban' },
  ],
  staff: [
    { title: 'Show the team', description: 'Active staff list', icon: 'Users' },
    { title: "Who's available this week?", description: 'Availability grid', icon: 'Calendar' },
    { title: 'Send a team invite', description: 'Magic-link new staff', icon: 'UserPlus' },
  ],
};

const SUGGESTIONS_BY_ROLE: Record<string, PromptSuggestion[]> = {
  chef: [
    { title: 'What dishes are on the menu tonight?', description: 'Full menu overview', icon: 'UtensilsCrossed' },
    { title: 'What are the allergens for the lamb?', description: 'Allergen & dietary info', icon: 'AlertCircle' },
    { title: 'What specials should I know about?', description: 'Specials and 86s', icon: 'Sparkles' },
    { title: "What's the table drop line for the kingfish?", description: 'Service scripts', icon: 'MessageSquare' },
  ],
  bartender: [
    { title: "What's on the drinks menu?", description: 'Full beverage list', icon: 'GlassWater' },
    { title: 'What cocktails do we have?', description: 'Cocktail menu & specs', icon: 'Wine' },
    { title: 'What are our wine pairings for mains?', description: 'Food and wine pairing', icon: 'Grape' },
    { title: "What's in the Negroni?", description: 'Cocktail recipe & specs', icon: 'BookOpen' },
  ],
  staff: [
    { title: "What's on the menu tonight?", description: 'Food & drink overview', icon: 'UtensilsCrossed' },
    { title: 'What are the GF options?', description: 'Dietary & allergen info', icon: 'Leaf' },
    { title: "What's the table drop line for the lamb?", description: 'Service scripts', icon: 'MessageSquare' },
    { title: 'What training do I have due?', description: 'My pending quizzes', icon: 'GraduationCap' },
  ],
};

function suggestionsFor(drawerId: string): PromptSuggestion[] {
  return SUGGESTIONS_BY_DRAWER[drawerId] ?? SUGGESTIONS_BY_DRAWER.general;
}

type Sug = PromptSuggestion & { prompt?: string };

/** Hour (0-23) and YYYY-MM-DD date in the venue's time zone. */
function zoned(tz: string, d = new Date()): { hour: number; date: string } {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(d);
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { hour: Number(g('hour')) % 24, date: `${g('year')}-${g('month')}-${g('day')}` };
}

/**
 * Chat-start suggestions built for this person from what is waiting for them.
 * Rule-based (a few small counts), no AI call. Falls back to the general set.
 */
function usePersonalSuggestions(venueId: string | undefined, userId: string | undefined, role: string | undefined, tz: string, enabled: boolean): Sug[] | null {
  const [list, setList] = useState<Sug[] | null>(null);
  useEffect(() => {
    if (!enabled || !venueId || !userId) { setList(null); return; }
    let off = false;
    const isManager = role === 'owner' || role === 'manager';
    const { hour, date } = zoned(tz);
    const evening = hour >= 17 || hour < 4;
    // The trading night: before 5am still counts as yesterday.
    const night = hour < 5 ? zoned(tz, new Date(Date.now() - 24 * 3_600_000)).date : date;
    const nowIso = new Date().toISOString();
    const count = async (q: PromiseLike<{ count: number | null; error: unknown }>) => {
      try { const r = await q; return r.error ? 0 : r.count ?? 0; } catch { return 0; }
    };

    void (async () => {
      const out: Sug[] = [];
      if (isManager) {
        const [toCheck, drafts, numbers] = await Promise.all([
          count(supabase.from('invoices').select('id', { count: 'exact', head: true }).eq('venue_id', venueId).eq('status', 'review')),
          count(supabase.from('shifts').select('id', { count: 'exact', head: true }).eq('venue_id', venueId).eq('published', false).gte('start_time', nowIso)),
          evening ? count(supabase.from('sales_days').select('trade_date', { count: 'exact', head: true }).eq('venue_id', venueId).eq('trade_date', night).eq('status', 'submitted')) : Promise.resolve(1),
        ]);
        if (evening && numbers === 0) out.push({ title: "Do tonight's numbers", description: 'Nightly numbers not submitted yet', icon: 'Calculator', prompt: "Help me do tonight's numbers" });
        if (toCheck > 0) out.push({ title: `Check ${toCheck} invoice${toCheck === 1 ? '' : 's'} waiting`, description: 'Read by AI, waiting for a check', icon: 'Receipt', prompt: 'Show me the invoices waiting to be checked' });
        if (drafts > 0) out.push({ title: `Publish ${drafts} draft shift${drafts === 1 ? '' : 's'}`, description: 'Staff can’t see drafts yet', icon: 'CalendarCheck', prompt: 'Show me the draft shifts that are not published yet' });
        out.push(hour < 12
          ? { title: 'Brief me for today', description: 'Forecast, weather and who’s on', icon: 'Sun' }
          : { title: "Who's on tonight?", description: "Tonight's roster", icon: 'Users' });
        out.push({ title: 'How did last week go?', description: 'Sales, covers and costs', icon: 'TrendingUp' });
      } else {
        const [training, shiftRes] = await Promise.all([
          count(supabase.from('training_assignments').select('id', { count: 'exact', head: true }).eq('venue_id', venueId).eq('user_id', userId).is('completed_at', null)),
          supabase.from('shifts').select('start_time, end_time').eq('venue_id', venueId).eq('user_id', userId).eq('published', true)
            .gte('end_time', nowIso).order('start_time').limit(1).then((r) => r, () => ({ data: null })),
        ]);
        const next = ((shiftRes as { data: Array<{ start_time: string; end_time: string }> | null }).data ?? [])[0];
        const onToday = !!next && zoned(tz, new Date(next.start_time)).date === date;
        if (onToday && evening) out.push({ title: 'Do my end-of-shift check-in', description: 'Quick notes for the manager', icon: 'ClipboardCheck', prompt: 'I want to do my end-of-shift check-in' });
        if (training > 0) out.push({ title: `Start my training (${training} to do)`, description: 'Quizzes assigned to you', icon: 'GraduationCap', prompt: 'What training do I have to do?' });
        out.push(onToday
          ? { title: "What's on for my shift?", description: 'Specials, 86s and notes', icon: 'Clock', prompt: "What do I need to know for my shift today?" }
          : { title: 'When am I next working?', description: 'Your next shift', icon: 'Calendar' });
        out.push({ title: "What's on the menu tonight?", description: 'Food and drinks', icon: 'UtensilsCrossed' });
        out.push({ title: 'What are the GF options?', description: 'Dietary and allergens', icon: 'Leaf' });
      }
      if (!off) setList(out.slice(0, 4));
    })();
    return () => { off = true; };
  }, [venueId, userId, role, tz, enabled]);
  return list;
}

export default function Welcome({ firstName, venueName, venueId, userId, timezone, drawerId, onSelectSuggestion, onOpenPanel, openPanelLabel, userRole, composer }: WelcomeProps) {
  const c = useAppColors();
  const tz = timezone || 'Australia/Sydney';
  const greeting = timeAwareGreeting(tz, firstName || null);
  const dayOfWeek = dayOfWeekIn(tz);
  // Role-based suggestion overrides (SUGGESTIONS_BY_ROLE) are disabled for now —
  // always show the 3-item general/drawer set instead of a 4-item role-specific
  // one, per request. The data is left in place above in case this comes back.
  const personalOn = !!venueId && !!userId && (!SUGGESTIONS_BY_DRAWER[drawerId] || drawerId === 'general');
  const personal = usePersonalSuggestions(venueId, userId, userRole, tz, personalOn);
  // While the personal list loads, show nothing rather than flashing the generic set.
  const suggestions: Sug[] = personalOn ? personal ?? [] : suggestionsFor(drawerId);
  const { fields: venueFields } = useVenueSetupFields(venueId);
  const weather = useWeather(
    (venueFields?.latitude as number | null) ?? null,
    (venueFields?.longitude as number | null) ?? null,
  );

  const containerAnimations = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3, staggerChildren: 0.05 } },
  };

  const itemAnimations = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      variants={containerAnimations}
      initial="initial"
      animate="animate"
      className="flex flex-col items-center justify-center min-h-full px-2 py-6 text-center"
    >
      {/* Greeting — pinned near the top */}
      <motion.h1
        variants={itemAnimations}
        className="text-[34px] font-semibold tracking-tight leading-tight"
        style={{ color: c.text }}
      >
        {greeting}
      </motion.h1>

      <motion.div
        variants={itemAnimations}
        className="flex items-center justify-center flex-wrap gap-x-2 gap-y-1 mt-2 text-sm font-medium"
        style={{ color: c.textDim }}
      >
        <span>{venueName} · {dayOfWeek}</span>
        {weather && (
          <span className="flex items-center gap-1.5">
            <span>{weather.icon}</span>
            <span>{weather.temp}°C</span>
            <span>|</span>
            <span>{weather.description}</span>
          </span>
        )}
      </motion.div>

      {/* The message box sits right under the greeting until the chat starts */}
      <div className="w-full mt-8 text-left">{composer}</div>

      {/* Suggestions: small, quiet rows under the box, lined up with its text */}
      <div className="w-full px-4 mt-3">
        <div className="w-full max-w-3xl mx-auto flex flex-col items-start">
          {suggestions.map((s) => {
            const Icon = (Icons as unknown as Record<string, LucideIcon | undefined>)[s.icon];
            return (
              <motion.button
                key={s.title}
                variants={itemAnimations}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelectSuggestion(s.prompt ?? s.title)}
                title={s.description}
                className="w-full text-left rounded-xl px-5 py-2.5 flex items-center gap-3 transition-colors"
                style={{ color: c.textDim }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = c.iconBg; (e.currentTarget as HTMLButtonElement).style.color = c.text; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = c.textDim; }}
              >
                {Icon ? <Icon size={16} className="flex-shrink-0" style={{ color: c.iconColor }} /> : null}
                <span className="text-[15px] truncate">{s.title}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {onOpenPanel && openPanelLabel && (
        <motion.button
          variants={itemAnimations}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={onOpenPanel}
          className="mt-6 px-4 py-2 rounded-full text-[11px] font-bold tracking-wider uppercase transition-all"
          style={{
            background: c.accentBg,
            border: `1px solid ${c.accentBorder}`,
            color: c.accentSoft,
          }}
        >
          {openPanelLabel}
        </motion.button>
      )}
    </motion.div>
  );
}
